/**
 * MappingModal — generic super-admin re-assignment dialog used to:
 *   1. Map an advisor to a manager
 *   2. Map a client to an advisor
 *
 * The same modal handles both flows by passing different option lists and
 * handler functions in.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaUserCircle, FaSpinner, FaSearch, FaUnlink } from 'react-icons/fa';

const MappingModal = ({
  isOpen,
  onClose,
  title,
  subjectLabel,         // "Advisor" or "Client"
  subjectName,          // the name we are remapping
  targetLabel,          // "Manager" or "Advisor"
  options,              // [{ id, name, email?, subtitle? }, ...]
  currentTargetId,
  onSubmit,             // (newTargetId | null) => Promise
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(currentTargetId || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelected(currentTargetId || null);
    setSearch('');
    setError(null);
  }, [isOpen, currentTargetId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o =>
      (o.name || '').toLowerCase().includes(q) ||
      (o.email || '').toLowerCase().includes(q) ||
      (o.subtitle || '').toLowerCase().includes(q)
    );
  }, [options, search]);

  if (!isOpen) return null;

  const submit = async (targetId) => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(targetId);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save the mapping. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => submit(selected);
  const handleUnassign = () => submit(null);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="mapping-modal">
      <div className="bg-gradient-to-br from-[#1A2942] to-[#0F172A] rounded-2xl border border-[#FFD700]/30 shadow-2xl max-w-md w-full max-h-[88vh] flex flex-col">
        <div className="p-5 border-b border-[#2D5A8F]/60 flex items-start justify-between gap-3">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider">{title}</p>
            <h2 className="text-white font-bold text-lg leading-snug mt-0.5">
              {subjectLabel}: <span className="text-[#FFD700]">{subjectName}</span>
            </h2>
            <p className="text-gray-500 text-xs mt-1">Select a {targetLabel.toLowerCase()} below.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="mapping-modal-close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5 pb-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
            <input
              type="search"
              placeholder={`Search ${targetLabel.toLowerCase()}s...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="mapping-modal-search"
              className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700]/60"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          {filtered.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">No matches found.</p>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map(opt => {
                const isSel = selected === opt.id;
                return (
                  <li key={opt.id}>
                    <button
                      onClick={() => setSelected(opt.id)}
                      data-testid={`mapping-option-${opt.id}`}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-all text-left ${
                        isSel
                          ? 'bg-[#FFD700]/10 border-[#FFD700]/60'
                          : 'bg-[#0F172A]/60 border-[#2D5A8F]/60 hover:border-[#FFD700]/30'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-[#FFD700] text-[#0A1929]' : 'bg-[#1E3A5F] text-gray-400'}`}>
                        <FaUserCircle size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{opt.name}</p>
                        {(opt.subtitle || opt.email) && (
                          <p className="text-gray-500 text-xs truncate">{opt.subtitle || opt.email}</p>
                        )}
                      </div>
                      {isSel && (
                        <span className="text-[10px] font-bold uppercase text-[#FFD700]">Selected</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error && (
          <div className="mx-5 mt-3 bg-red-500/10 border border-red-500/40 text-red-300 text-xs rounded p-2">
            {error}
          </div>
        )}

        <div className="p-5 pt-3 border-t border-[#2D5A8F]/60 flex flex-col sm:flex-row gap-2">
          {currentTargetId && (
            <button
              onClick={handleUnassign}
              disabled={saving}
              data-testid="mapping-unassign-btn"
              className="px-3 py-2 border border-red-500/50 text-red-300 text-sm rounded-lg hover:bg-red-500/10 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <FaUnlink size={11} /> Unassign current
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selected || selected === currentTargetId}
            data-testid="mapping-save-btn"
            className="px-5 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (<><FaSpinner className="animate-spin" /> Saving...</>) : 'Save Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingModal;
